import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy 
} from 'firebase/firestore';

const USERS_COLLECTION = 'users';

// Create a new user
export const createUser = async (userData) => {
  try {
    // Check if user already exists
    const existingUser = await getUserByEmail(userData.email);
    if (existingUser) {
      throw new Error('User with this email already exists');
    }

    const userRef = await addDoc(collection(db, USERS_COLLECTION), {
      ...userData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    return { id: userRef.id, ...userData };
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

// Get all users
export const getAllUsers = async () => {
  try {
    const usersQuery = query(
      collection(db, USERS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(usersQuery);
    const users = [];
    
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    return users;
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

// Get user by ID
export const getUserById = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId));
    
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user by ID:', error);
    throw error;
  }
};

// Get user by email
export const getUserByEmail = async (email) => {
  try {
    const usersQuery = query(
      collection(db, USERS_COLLECTION),
      where('email', '==', email)
    );
    
    const querySnapshot = await getDocs(usersQuery);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user by email:', error);
    throw error;
  }
};

// Update user
export const updateUser = async (userId, userData) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      ...userData,
      updatedAt: new Date().toISOString()
    });
    
    return { id: userId, ...userData };
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// Delete user
export const deleteUser = async (userId) => {
  try {
    await deleteDoc(doc(db, USERS_COLLECTION, userId));
    return true;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// Initialize default admin user if it doesn't exist
export const initializeDefaultAdmin = async () => {
  try {
    console.log('Checking for existing admin user...');
    
    // Get all users and check if admin exists
    const allUsers = await getAllUsers();
    const existingAdmin = allUsers.find(user => user.email === 'admin@bullbearly.com');
    
    console.log('Existing admin found:', !!existingAdmin);
    
    if (!existingAdmin) {
      console.log('Creating default admin user...');
      const defaultAdmin = {
        fullName: 'Admin User',
        email: 'admin@bullbearly.com',
        phone: '+1-555-0123',
        role: 'ADMIN',
        subscriptionType: 'MANAGED',
        password: 'admin', // In production, this should be hashed
        authProvider: 'email',
        googleUid: null,
        profilePicture: ''
      };
      
      // Use addDoc directly to avoid the duplicate check in createUser
      const userRef = await addDoc(collection(db, USERS_COLLECTION), {
        ...defaultAdmin,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      console.log('Default admin user created with ID:', userRef.id);
    } else {
      console.log('Admin user already exists, skipping creation');
    }
  } catch (error) {
    console.error('Error initializing default admin:', error);
    // Don't throw error to prevent app from breaking
    console.log('Continuing without default admin user');
  }
};
